import isNil from "lodash/isNil";
import { GroupOption, Option, OptionOriginal } from "PFTypes/options";
import { Dispatch, MutableRefObject, SetStateAction, useState } from "react";

import { SearchableVariant, SelectProps } from "../select.types";
import { flattenOptions } from "../select.utils";
import { checkIsSearchable } from "./dropdown.utils";
import { getKeyDownHandler, GetKeyDownHandlerReturn } from "./get_key_down_handler";
import { useDropdownOptions, UseDropdownOptionsReturn } from "./use_dropdown_options";
import { HighlightedIndex, useHighlightedIndex } from "./use_highlighted_index";
import { useOptionCreate } from "./use_option_create";

type UseDropdown<T> = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  triggerRef: MutableRefObject<HTMLDivElement | null>;
  dropdownId: string;
} & Pick<
  SelectProps<T>,
  | "searchId"
  | "value"
  | "options"
  | "fetchOptions"
  | "multiple"
  | "onChange"
  | "searchableVariant"
  | "onCreateOption"
  | "createOptionBlocklist"
  | "cacheTime"
  | "parseOptions"
  | "limit"
>;

export type DropdownData<T> = UseDropdownOptionsReturn<T> &
  GetKeyDownHandlerReturn & {
    isSearchable: boolean;
    searchTerm: string;
    setSearchTerm: Dispatch<SetStateAction<string>>;
    highlightedIndex: HighlightedIndex;
    setHighlightedIndex: Dispatch<SetStateAction<HighlightedIndex>>;
    handleCreateOption?: VoidFunction;
    handleChange: (option: Option<T>, selected: boolean) => void;
  };

export const useDropdown = <T extends OptionOriginal = OptionOriginal>({
  dropdownId,
  searchId,
  isOpen,
  setIsOpen,
  value,
  triggerRef,
  options,
  fetchOptions,
  parseOptions,
  multiple,
  onChange,
  onCreateOption,
  createOptionBlocklist,
  searchableVariant = SearchableVariant.Dynamic,
  limit,
  cacheTime = 300000
}: UseDropdown<T>): DropdownData<T> => {
  const [searchTerm, setSearchTerm] = useState<string>("");

  const isSearchable = checkIsSearchable(
    searchableVariant,
    flattenOptions(options || []).length ?? 0,
    !!fetchOptions
  );

  const dropdownOptions = useDropdownOptions<T>({
    dropdownId,
    searchId,
    searchTerm,
    value,
    options,
    fetchOptions,
    parseOptions,
    isDropdownOpen: isOpen,
    cacheTime,
    multiple,
    onChange,
    isSearchable,
    limit
  });

  const {
    resultOptions,
    selectedOptions,
    isFetchingAvailableOptions,
    isFetchingSelectedOptions,
    isFetchingOptionsEnabled
  } = dropdownOptions;

  const isFetching = isFetchingAvailableOptions || isFetchingSelectedOptions;

  const { handleCreateOption } = useOptionCreate({
    searchTerm,
    onCreateOption,
    createOptionBlocklist,
    setIsOpen,
    dropdownId,
    isFetchingOptionsEnabled,
    resultOptions,
    selectedOptions,
    isFetching
  });

  const [highlightedIndex, setHighlightedIndex] = useHighlightedIndex({
    optionsCount: resultOptions.length,
    isFetching
  });

  const getGroupOptionResultSelection = (option: Option<T>) => {
    const group = (resultOptions as GroupOption<T>[]).find(({ options }) =>
      options.some(({ id }) => id === option.id)
    );
    if (!group) {
      throw new Error(`Group for option: ${option.id} not found`);
    }
    const selectedOptionIds = selectedOptions.map(({ id }) => id);
    const alreadySelectedIdsInGroup = group.options
      .filter(({ id }) => selectedOptionIds.includes(id))
      .map(({ id }) => id);

    if (isNil(group.selectionLimit) || group.selectionLimit > alreadySelectedIdsInGroup.length) {
      return [...selectedOptions, option];
    } else if (group.selectionLimit === 1) {
      return [...selectedOptions.filter(({ id }) => !alreadySelectedIdsInGroup.includes(id)), option];
    } else {
      return selectedOptions;
    }
  };

  const handleChange = (option: Option<T>, selected: boolean) => {
    if (!multiple) {
      setIsOpen(false);
      return onChange(option);
    }

    if (!selected) {
      return onChange(selectedOptions.filter(({ id }) => id !== option.id));
    }

    const { areOptionsGrouped } = dropdownOptions;
    const resultSelection = areOptionsGrouped
      ? getGroupOptionResultSelection(option)
      : [...selectedOptions, option];
    return onChange(resultSelection);
  };

  const { handleKeyDown } = getKeyDownHandler({
    value,
    triggerRef,
    options: flattenOptions(resultOptions),
    onOptionChange: handleChange,
    setIsOpen,
    highlightedIndex,
    setHighlightedIndex
  });

  return {
    ...dropdownOptions,
    isSearchable,
    searchTerm,
    setSearchTerm,
    highlightedIndex,
    setHighlightedIndex,
    handleKeyDown,
    handleCreateOption,
    handleChange
  };
};
